import dayjs from 'dayjs'

// Which links schedule onboarding with the Influencer experience.
//
// Influencer 1:Many links carry an influencer_name. Partner 1:Many links have
// none, no order_id (that marks a 1:1 link) and never arrive with linkType
// '1:1'. Both are scheduled the same way: the customer books onboarding after
// the account details and before payment, and the 48h-to-the-15th window and
// InfluencerOps service-id rules apply.

const hasValue = value => value !== null && value !== undefined && value !== ''

export const isInfluencerLink = linkDetail =>
  hasValue(linkDetail?.influencer_name)

export const isPartnerOneToManyLink = (linkDetail, linkType) =>
  Boolean(linkDetail) &&
  !isInfluencerLink(linkDetail) &&
  !hasValue(linkDetail?.order_id) &&
  linkType !== '1:1'

// True for every link that books onboarding the Influencer way.
export const usesInfluencerScheduling = (linkDetail, linkType) =>
  isInfluencerLink(linkDetail) || isPartnerOneToManyLink(linkDetail, linkType)

// ---------------------------------------------------------------------------
// Bookable date window
//
// Influencer / Partner 1:Many: at least 48 hours from now, at most 30 days
// out, and never past the 15th (this month's if today is on/before the 15th,
// otherwise next month's). With new SEO in the cart the minimum is pushed out
// to offset_start_day days (see below) when that is later than 48 hours.
//
// Every other keap partner link keeps the legacy window: today (or the day
// after the kickoff call for the creative call) up to a weekday-based
// extension. New SEO is the exception: it starts offset_start_day days out
// (5 by default) and ends offset_end_day days out (30 by default), both
// counted from today.
//
// `now` is injectable so the rules can be tested.
// ---------------------------------------------------------------------------

export const INFLUENCER_MIN_HOURS = 48
export const INFLUENCER_MAX_DAYS = 30
export const INFLUENCER_CUTOFF_DAY = 15

export const getInfluencerMinDate = (now = dayjs()) =>
  now.add(INFLUENCER_MIN_HOURS, 'hours').toDate()

export const getInfluencerMaxDate = (now = dayjs()) => {
  const thirtyDaysOut = now.add(INFLUENCER_MAX_DAYS, 'days')
  let maxDate = thirtyDaysOut
  const currentDay = now.date()
  const maxDay = maxDate.date()

  // Today is after the 15th, or 30 days out would land past the 15th:
  // stop at the 15th of this or next month.
  if (
    currentDay > INFLUENCER_CUTOFF_DAY ||
    (currentDay <= INFLUENCER_CUTOFF_DAY && maxDay > INFLUENCER_CUTOFF_DAY)
  ) {
    maxDate =
      currentDay > INFLUENCER_CUTOFF_DAY
        ? now.add(1, 'month').date(INFLUENCER_CUTOFF_DAY)
        : now.date(INFLUENCER_CUTOFF_DAY)
  }

  // Never beyond 30 days.
  if (maxDate.isAfter(thirtyDaysOut)) {
    maxDate = thirtyDaysOut
  }

  return maxDate.toDate()
}

// New SEO: the bookable window is days from today, read from
// product_schedule_offset_days (cc/order/schedule-offset-days: offset_start_day
// / offset_end_day), the same source ooe-app's schedulers use. These defaults
// apply only when that lookup fails or has no SEO row: 5 days minimum, 30 days
// maximum.
export const SEO_DEFAULT_OFFSET_START_DAYS = 5
export const SEO_DEFAULT_OFFSET_END_DAYS = 30

const positiveDays = value =>
  Number.isFinite(Number(value)) && Number(value) > 0
    ? Number(value)
    : undefined

// The SEO row of the offset-days response, as {seoOffsetStartDays,
// seoOffsetEndDays}; either is undefined when missing or not a positive number.
export const getSeoScheduleOffsets = (offsetDetails, seoProductCodes = []) => {
  const row = (Array.isArray(offsetDetails) ? offsetDetails : []).find(detail =>
    seoProductCodes.includes(detail?.product_code),
  )
  return {
    seoOffsetStartDays: positiveDays(row?.offset_start_day),
    seoOffsetEndDays: positiveDays(row?.offset_end_day),
  }
}

export const getStandardMinDate = ({
  now = dayjs(),
  isCreativeCallTabActive = false,
  isKickOffCallExist = false,
  isKickoffScheduled = false,
  kickoffBookingDate,
  isNewSEO = false,
  seoOffsetStartDays = SEO_DEFAULT_OFFSET_START_DAYS,
} = {}) => {
  if (isCreativeCallTabActive && isKickOffCallExist && isKickoffScheduled) {
    return dayjs(kickoffBookingDate).add(1, 'day').toDate()
  }
  if (isNewSEO) {
    return now.add(seoOffsetStartDays, 'day').toDate()
  }
  return now.toDate()
}

export const getStandardMaxDate = ({
  now = dayjs(),
  country = 'US',
  isCreativeCallTabActive = false,
  isNewSEO = false,
  seoOffsetEndDays = SEO_DEFAULT_OFFSET_END_DAYS,
  minDate,
} = {}) => {
  const min = dayjs(minDate ?? getStandardMinDate({now}))
  if (isNewSEO && !isCreativeCallTabActive) {
    return now.add(seoOffsetEndDays, 'day').toDate()
  }
  // Extra days to offer, indexed by today's weekday (Sun..Sat).
  let dateAdds = [13, 13, 13, 13, 13, 13, 13]
  if (isCreativeCallTabActive && (country === 'AU' || country === 'NZ')) {
    dateAdds = [16, 16, 16, 16, 18, 18, 17]
  }
  return min.add(dateAdds[now.day()], 'day').toDate()
}

// {min, max} for the calendar. `max` may fall before `min` (e.g. 14 Feb:
// 48h out is the 16th, but the cut-off is the 15th) — callers must treat that
// as "no dates available" (see hasBookableDates).
export const getScheduleDateWindow = ({
  isInfluencerOps = false,
  now = dayjs(),
  ...standardOptions
} = {}) => {
  if (isInfluencerOps) {
    let min = getInfluencerMinDate(now)
    let max = getInfluencerMaxDate(now)
    // New SEO needs preparation time on these links too: it can't start before
    // today + offset_start_day (5 by default) nor end after today +
    // offset_end_day (30 by default). The 48h minimum and the 15th cut-off
    // above still apply; whichever is stricter wins.
    const {
      isNewSEO = false,
      isCreativeCallTabActive = false,
      seoOffsetStartDays = SEO_DEFAULT_OFFSET_START_DAYS,
      seoOffsetEndDays = SEO_DEFAULT_OFFSET_END_DAYS,
    } = standardOptions
    if (isNewSEO && !isCreativeCallTabActive) {
      const seoMin = now.add(seoOffsetStartDays, 'day').toDate()
      const seoMax = now.add(seoOffsetEndDays, 'day').toDate()
      if (seoMin > min) min = seoMin
      if (seoMax < max) max = seoMax
    }
    return {min, max}
  }
  const min = getStandardMinDate({now, ...standardOptions})
  return {
    min,
    max: getStandardMaxDate({
      now,
      minDate: min,
      country: standardOptions.country,
      isCreativeCallTabActive: standardOptions.isCreativeCallTabActive,
      isNewSEO: standardOptions.isNewSEO,
      seoOffsetEndDays: standardOptions.seoOffsetEndDays,
    }),
  }
}

// Whole-day comparison — the time of day on either bound never matters.
export const hasBookableDates = ({min, max}) =>
  !dayjs(max).startOf('day').isBefore(dayjs(min).startOf('day'))
